import { useState, useEffect, useRef } from 'react'
import { useInstrumentSearch } from '../hooks/useInstrumentSearch'
import { usePrefetchTicker } from '../hooks/usePrefetchTicker'
import { useRecentTickersStore } from '../stores/useRecentTickersStore'
import type { InstrumentDetail } from '../api/types'

interface Props {
  onSelect: (symbol: string) => void
  variant?: 'standalone' | 'header'
  /**
   * Install the document-level "type anywhere to search" listener. The header
   * search owns this globally; secondary instances (e.g. the charts-page cell
   * picker) pass false so two search boxes don't both grab the keystroke.
   */
  captureTyping?: boolean
  /** Focus the input and open the dropdown on mount. */
  autoFocus?: boolean
  /** Initial query text (used to seed the box with the first typed character). */
  seedQuery?: string
  /** Called when the user dismisses the box (Escape) or commits a pick. */
  onClose?: () => void
}

export function SearchBar({
  onSelect,
  variant = 'standalone',
  captureTyping = true,
  autoFocus = false,
  seedQuery = '',
  onClose,
}: Props) {
  const [query, setQuery] = useState(seedQuery)
  const [open, setOpen] = useState(autoFocus)
  const [highlight, setHighlight] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const prefetchTicker = usePrefetchTicker()

  const { results, isSyncing } = useInstrumentSearch(query, {
    type: 'EQUITY',
    limit: 20,
  })
  const recentTickers = useRecentTickersStore((s) => s.tickers)
  const addTicker = useRecentTickersStore((s) => s.add)
  const removeTicker = useRecentTickersStore((s) => s.remove)
  const hasInstruments = results.length > 0 || query.trim().length === 0

  const showRecents = query.trim().length === 0 && recentTickers.length > 0
  const recentSet = new Set(recentTickers)
  const recentItems: InstrumentDetail[] = showRecents
    ? recentTickers.map((symbol) => ({ instrument: { symbol }, trading: 'BUY_AND_SELL' } as InstrumentDetail))
    : []
  const combined: InstrumentDetail[] = [
    ...recentItems,
    ...results.filter((r) => !showRecents || !recentSet.has(r.instrument.symbol)),
  ]

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    if (autoFocus) {
      const input = inputRef.current
      if (input) {
        input.focus()
        const len = input.value.length
        input.setSelectionRange(len, len)
      }
    }
  }, [autoFocus])

  useEffect(() => {
    if (!captureTyping) return
    function handleKey(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      if (e.key.length !== 1 || !/[a-zA-Z0-9]/.test(e.key)) return
      const target = e.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target.isContentEditable
        ) {
          return
        }
      }
      const input = inputRef.current
      if (!input || input.disabled) return
      e.preventDefault()
      const char = e.key
      setQuery(char)
      setOpen(true)
      input.focus()
      requestAnimationFrame(() => {
        input.setSelectionRange(char.length, char.length)
      })
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [captureTyping])

  function handlePick(d: InstrumentDetail) {
    addTicker(d.instrument.symbol)
    onSelect(d.instrument.symbol)
    setQuery('')
    setOpen(false)
    setHighlight(0)
    inputRef.current?.blur()
    onClose?.()
  }

  function handleRemoveRecent(symbol: string, e: React.MouseEvent) {
    e.stopPropagation()
    removeTicker(symbol)
  }

  function handleEnter() {
    const pick = combined[highlight] ?? combined[0]
    if (pick) handlePick(pick)
  }

  useEffect(() => {
    setHighlight(0)
  }, [query])

  useEffect(() => {
    itemRefs.current[highlight]?.scrollIntoView({ block: 'nearest' })
  }, [highlight])

  const isHeader = variant === 'header'
  const containerClass = isHeader ? 'relative h-full' : 'relative'
  const inputClass = isHeader
    ? 'w-full h-full bg-transparent border-0 border-x border-zinc-800 rounded-none px-4 text-zinc-100 font-mono uppercase focus:outline-none focus:bg-zinc-900 disabled:opacity-50'
    : 'w-full bg-zinc-900 border border-zinc-700 rounded px-4 py-3 text-zinc-100 font-mono uppercase focus:outline-none focus:border-emerald-500 disabled:opacity-50'
  const dropdownClass = isHeader
    ? 'absolute left-0 right-0 top-full z-20 bg-zinc-900 border border-zinc-800 border-t-0 shadow-xl max-h-80 overflow-y-auto'
    : 'absolute z-10 mt-1 w-full bg-zinc-900 border border-zinc-700 rounded shadow-xl max-h-80 overflow-y-auto'

  return (
    <div ref={containerRef} className={containerClass}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            handleEnter()
          } else if (e.key === 'ArrowDown') {
            e.preventDefault()
            if (combined.length === 0) return
            setOpen(true)
            setHighlight((h) => (h + 1) % combined.length)
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            if (combined.length === 0) return
            setOpen(true)
            setHighlight((h) => (h <= 0 ? combined.length - 1 : h - 1))
          } else if (e.key === 'Escape') {
            setOpen(false)
            setHighlight(0)
            onClose?.()
          }
        }}
        placeholder={
          isSyncing && !hasInstruments
            ? 'Loading instruments…'
            : 'Search ticker (e.g. AAPL)'
        }
        className={inputClass}
      />
      {open && combined.length > 0 && (
        <div className={dropdownClass}>
          {combined.map((r, i) => {
            const symbol = r.instrument.symbol
            const isRecent = showRecents && i < recentItems.length
            return (
              <button
                key={`${isRecent ? 'recent' : 'result'}-${symbol}`}
                ref={(el) => {
                  itemRefs.current[i] = el
                }}
                type="button"
                onClick={() => handlePick(r)}
                onMouseEnter={() => {
                  setHighlight(i)
                  prefetchTicker(symbol)
                }}
                className={`group w-full text-left px-4 py-2 flex justify-between items-center ${
                  i === highlight ? 'bg-zinc-800' : 'hover:bg-zinc-800'
                }`}
              >
                <span className="font-mono text-zinc-100">{symbol}</span>
                {isRecent ? (
                  <span className="flex items-center gap-2">
                    <span className="text-xs text-zinc-500">recent</span>
                    <span
                      role="button"
                      tabIndex={-1}
                      onClick={(e) => handleRemoveRecent(symbol, e)}
                      className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-zinc-300 text-lg leading-none px-1"
                    >
                      ×
                    </span>
                  </span>
                ) : (
                  <span className="text-xs text-zinc-500">
                    {r.trading === 'BUY_AND_SELL' ? 'tradeable' : r.trading.toLowerCase()}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
